import { Globe, Save, Clock } from 'lucide-react';
import React, { useState } from 'react';

type PostStatus = 'draft' | 'scheduled' | 'published' | 'archived';

interface Props {
  status: PostStatus;
  saving: boolean;
  onSave: (status: PostStatus) => void;
  scheduledFor?: string;
  onScheduledForChange?: (val: string) => void;
}

export const StatusBar: React.FC<Props> = ({
  status, saving, onSave, scheduledFor = '', onScheduledForChange,
}) => {
  const [showSchedule, setShowSchedule] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Status badge */}
        <span
          className={`ds-chip uppercase ${
            status === 'published'
              ? 'ds-chip-ok'
              : status === 'scheduled'
              ? 'ds-chip-info'
              : status === 'archived'
              ? ''
              : 'ds-chip-warn'
          }`}
        >
          {status}
        </span>

        <div className="flex-1" />

        {/* Save draft — secondary outline, always readable in dark + light */}
        <button
          type="button"
          onClick={() => onSave('draft')}
          disabled={saving}
          className="ds-btn ds-btn-secondary disabled:opacity-50 min-h-[44px] touch-manipulation"
        >
          <Save className="w-4 h-4" />
          {saving && status === 'draft' ? 'Saving…' : 'Save draft'}
        </button>

        {/* Schedule — ghost, slightly muted but legible */}
        <button
          type="button"
          onClick={() => setShowSchedule((v) => !v)}
          disabled={saving}
          className="ds-btn ds-btn-ghost disabled:opacity-50 min-h-[44px] touch-manipulation"
        >
          <Clock className="w-4 h-4" />
          Schedule
        </button>

        {/* Publish — primary CTA, branded fill, always high-contrast */}
        <button
          type="button"
          onClick={() => onSave('published')}
          disabled={saving}
          className="ds-btn ds-btn-primary disabled:opacity-50 min-h-[44px] touch-manipulation"
        >
          <Globe className="w-4 h-4" />
          {saving && status === 'published' ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {/* Schedule date picker */}
      {showSchedule && (
        <div className="ds-card-sm flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="t-label whitespace-nowrap">
            Publish at:
          </label>
          <input
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => onScheduledForChange?.(e.target.value)}
            className="ds-input ds-input-sm flex-1 min-h-[44px]"
          />
          <button
            type="button"
            onClick={() => { setShowSchedule(false); onSave('scheduled'); }}
            disabled={saving || !scheduledFor}
            className="ds-btn ds-btn-primary whitespace-nowrap disabled:opacity-50 min-h-[44px] touch-manipulation"
          >
            {saving ? 'Scheduling…' : 'Confirm schedule'}
          </button>
        </div>
      )}
    </div>
  );
};
